import { useState, useEffect } from 'react';
import { getAdminConversionProjections } from '../../api';
import { UserGroupIcon, ClockIcon } from '@heroicons/react/24/outline';

export default function ConversionProjectionsPage() {
  const [projectionData, setProjectionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProjections = async () => {
      try {
        const data = await getAdminConversionProjections();
        setProjectionData(data);
        if (data.error) {
          setError(data.error);
        } else {
          setError(null);
        }
      } catch (err) {
        setError('Failed to load conversion projections');
        console.error('Error fetching projections:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProjections();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold text-foreground mb-6">Conversion Projections</h1>
        <div className="bg-background-elevated border border-border rounded-lg shadow-lg p-6">
          <div className="flex items-center mb-4">
            <UserGroupIcon className="h-6 w-6 text-accent mr-2" />
            <h2 className="text-xl font-semibold text-foreground">No Data Available</h2>
          </div>
          <div className="bg-danger-soft border border-danger-border rounded-md p-4">
            <p className="text-danger">{error}</p>
            <p className="text-danger/80 mt-2 text-sm">Conversion projections will be available once users start using their free credits.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!projectionData || !projectionData.projections || projectionData.projections.length === 0) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold text-foreground mb-6">Conversion Projections</h1>
        <div className="bg-background-elevated border border-border rounded-lg shadow-lg p-6">
          <div className="flex items-center mb-4">
            <UserGroupIcon className="h-6 w-6 text-accent mr-2" />
            <h2 className="text-xl font-semibold text-foreground">No Data Available</h2>
          </div>
          <p className="text-muted-foreground">No conversion projections available. Check back once users start using their free credits.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 text-foreground">
      <h1 className="text-2xl font-semibold text-foreground mb-6">Conversion Projections</h1>

      {/* Summary Card */}
      <div className="bg-background-elevated border border-border rounded-lg shadow-lg p-6 mb-8">
        <div className="flex items-center mb-4">
          <UserGroupIcon className="h-6 w-6 text-accent mr-2" />
          <h2 className="text-xl font-semibold text-foreground">Users Likely to Convert</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-background-soft border border-border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Users About to Run Out</span>
              <UserGroupIcon className="h-5 w-5 text-accent" />
            </div>
            <div className="text-2xl font-bold text-accent mt-2">
              {projectionData.projections.length}
            </div>
          </div>
          <div className="bg-background-soft border border-border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Avg. Distinct Job Days</span>
              <ClockIcon className="h-5 w-5 text-info" />
            </div>
            <div className="text-2xl font-bold text-info mt-2">
              {(
                projectionData.projections.reduce((acc, curr) => acc + curr.num_distinct_job_days, 0) /
                projectionData.projections.length
              ).toFixed(1)}
            </div>
          </div>
          <div className="bg-background-soft border border-border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Avg. Credits Left</span>
              <UserGroupIcon className="h-5 w-5 text-success" />
            </div>
            <div className="text-2xl font-bold text-success mt-2">
              {(
                projectionData.projections.reduce((acc, curr) => acc + curr.compress_credits, 0) /
                projectionData.projections.length
              ).toFixed(1)}
            </div>
          </div>
        </div>
      </div>

      {/* User Table */}
      <div className="bg-background-elevated border border-border rounded-lg shadow-lg p-6">
        <div className="flex items-center mb-4">
          <UserGroupIcon className="h-6 w-6 text-accent mr-2" />
          <h2 className="text-xl font-semibold text-foreground">User-Level Conversion Projections</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border">
            <thead>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Credits Left</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Distinct Job Days</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Total Jobs</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">First Job Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Last Job Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {projectionData.projections.map((projection, index) => (
                <tr key={projection.user_id} className={index % 2 === 0 ? 'bg-background-elevated' : 'bg-background-soft'}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">{projection.username}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{projection.compress_credits}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{projection.num_distinct_job_days}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{projection.total_jobs}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{projection.first_job_date ? new Date(projection.first_job_date).toLocaleDateString() : '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{projection.last_job_date ? new Date(projection.last_job_date).toLocaleDateString() : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
} 